import { Injectable } from '@angular/core';
import { AngularFirestoreCollection } from '@angular/fire/firestore';

import { Observable, ReplaySubject, Subject } from 'rxjs';

import * as _ from 'lodash';

import { Channel, ChannelType, ChannelTypeKeys } from '../../models';

import { AuthenticationService } from '../authentication.service';
import { ChannelsService } from '../channels.service';
import { FirestoreService } from './firestore.service';

import { TranslateService } from '@ngx-translate/core';

interface ChannelDTO {
    allowOutbound: boolean;
    allowProactiveEscalation?: boolean;
    id: string;
    internalId: string;
    logo: string;
    name?: string;
    tagId?: string;
    translationKey?: string;
    type: string;
}

/**
 * Provides channels from firestore database.
 *
 * @export
 */
@Injectable()
export class FirestoreChannelsService implements ChannelsService {
    private _channelsSubject: Subject<Channel[]>;

    private get collection(): AngularFirestoreCollection<Channel> {
        return this.firestoreService.tenantDependentCollection<Channel>(this.authenticationService.user.tenantId, 'channels');
    }

    /**
     * Creates an instance of FirestoreChannelsService.
     */
    constructor(
        private authenticationService: AuthenticationService,
        private firestoreService: FirestoreService,
        private translateService: TranslateService
    ) {
        this._channelsSubject = new ReplaySubject<Channel[]>(1);

        this.init();
    }

    /**
     * Provides a comparison between channel types lists received as arguments.
     */
    validateChannelTypes(
        channelTypesListDestiny: ChannelType[],
        channelTypesListSource: ChannelType[],
        excludeItems: ChannelType[]
    ): boolean {
        channelTypesListSource = channelTypesListSource ?? ChannelTypeKeys;
        excludeItems = excludeItems ?? [];

        if (channelTypesListSource && channelTypesListSource.length > 0 && channelTypesListDestiny && channelTypesListDestiny.length > 0) {
            let source = (channelTypesListSource as string[]).map((el) => el.toLocaleLowerCase());
            const destiny = (channelTypesListDestiny as string[]).map((el) => el.toLocaleLowerCase());

            if (excludeItems && excludeItems.length > 0) {
                const sourceExcludedItems = (excludeItems as string[]).map((el) => el.toLocaleLowerCase());
                source = _.difference(source, sourceExcludedItems) as string[];
            }

            return _.intersection(source, destiny).length === channelTypesListDestiny.length;
        }

        return false;
    }

    /**
     * Provides an observable will all available channels for the current tenant.
     */
    channels(): Observable<Channel[]> {
        return this._channelsSubject.asObservable();
    }

    private async init(): Promise<any> {
        this.collection.snapshotChanges().subscribe((res) => {
            const channels = res.map((doc) => {
                const channelDTO = doc.payload.doc.data() as ChannelDTO;
                return {
                    allowOutbound: channelDTO.allowOutbound,
                    allowProactiveEscalation: !!channelDTO.allowProactiveEscalation,
                    id: doc.payload.doc.id,
                    internalId: channelDTO.internalId,
                    logo: channelDTO.logo,
                    name: channelDTO.translationKey ? this.translateService.instant(channelDTO.translationKey) : channelDTO.name,
                    tagId: channelDTO.tagId,
                    type: channelDTO.type as ChannelType,
                };
            });

            this._channelsSubject.next(channels);
        });
    }
}
